
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import { User, Mail, Briefcase, Calendar, Image as ImageIcon, Loader2, ChevronDown } from 'lucide-react';

const DEPARTMENTS = [
  'HR',
  'Tech',
  'Finance',
  'Sales',
  'Marketing',
  'Support',
];


export default function EmployeeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Form state
  const [form, setForm] = useState({
    name: '',
    email: '',
    department: '',
    role: '',
    joiningDate: '',
    profilePic: '', // base64 string
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef();

  // If edit mode, fetch employee data here (skipped for now)
  useEffect(() => {
    if (isEdit) {
      // Fetch and setForm with employee data by id
    }
  }, [isEdit, id]);

  // Handle input changes
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  // Handle file upload
  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors(errs => ({ ...errs, profilePic: 'Only image files allowed.' }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors(errs => ({ ...errs, profilePic: 'Max file size is 2MB.' }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(f => ({ ...f, profilePic: reader.result }));
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setFileName(file.name);
    setErrors(errs => ({ ...errs, profilePic: undefined }));
  }

  // Validate form
  function validate() {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Full Name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = 'Invalid email.';
    if (!form.department) errs.department = 'Department is required.';
    if (!form.role.trim()) errs.role = 'Role is required.';
    // joiningDate is optional
    return errs;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      // Simulate API call
      navigate('/employees');
    }, 1200);
  }

  function handleCancel() {
    navigate('/employees');
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto mt-10 bg-white/80 backdrop-blur-lg rounded-xl shadow-lg border border-gray-200 p-6 md:p-8 hover:shadow-xl transition-shadow">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm text-gray-500 flex gap-2 items-center">
          <Link to="/" className="hover:underline">Home</Link>
          <span>&gt;</span>
          <Link to="/employees" className="hover:underline">Employees</Link>
          <span>&gt;</span>
          <span className="text-gray-700 font-medium">{isEdit ? 'Edit Employee' : 'Add Employee'}</span>
        </nav>
        <h2 className="text-2xl font-bold text-gray-800 mb-1">{isEdit ? 'Edit Employee' : 'Add Employee'}</h2>
        <div className="text-xs text-gray-500 mb-7">Fill in the details below to {isEdit ? 'update' : 'add'} an employee.</div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`w-full pl-11 pr-4 py-3 rounded-lg bg-white border border-gray-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 shadow-sm transition duration-300 peer ${errors.name ? 'border-red-400' : ''}`}
                placeholder="John Carter"
                autoComplete="off"
                required
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
          </div>
          {/* Email */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`w-full pl-11 pr-4 py-3 rounded-lg bg-white border border-gray-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 shadow-sm transition duration-300 peer ${errors.email ? 'border-red-400' : ''}`}
                placeholder="john@email.com"
                autoComplete="off"
                required
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            {errors.email && <span className="text-xs text-red-500 mt-1">{errors.email}</span>}
          </div>
          {/* Department */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Department <span className="text-red-500">*</span></label>
            <div className="relative">
              <select
                name="department"
                value={form.department}
                onChange={handleChange}
                className={`w-full appearance-none pl-11 pr-10 py-3 rounded-lg bg-white border border-gray-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 shadow-sm transition duration-300 peer ${errors.department ? 'border-red-400' : ''}`}
                required
              >
                <option value="">Select department</option>
                {DEPARTMENTS.map(dep => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Briefcase className="h-5 w-5 text-gray-400" />
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            {errors.department && <span className="text-xs text-red-500 mt-1">{errors.department}</span>}
          </div>
          {/* Role */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Role <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="text"
                name="role"
                value={form.role}
                onChange={handleChange}
                className={`w-full pl-11 pr-4 py-3 rounded-lg bg-white border border-gray-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 shadow-sm transition duration-300 peer ${errors.role ? 'border-red-400' : ''}`}
                placeholder="e.g. Software Engineer"
                autoComplete="off"
                required
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Briefcase className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            {errors.role && <span className="text-xs text-red-500 mt-1">{errors.role}</span>}
          </div>
          {/* Joining Date */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Joining Date</label>
            <div className="relative">
              <input
                type="date"
                name="joiningDate"
                value={form.joiningDate}
                onChange={handleChange}
                className="w-full pl-11 pr-4 py-3 rounded-lg bg-white border border-gray-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 shadow-sm transition duration-300 peer"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">Optional</div>
          </div>
          {/* Profile Picture Upload */}
          <div className="md:col-span-2 mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
            <div className="flex items-center space-x-6">
              {/* Preview */}
              <div className="relative group">
                <div className="bg-gray-200 border-2 border-dashed rounded-full w-24 h-24 overflow-hidden flex items-center justify-center">
                  {preview ? (
                    <img src={preview} className="w-full h-full object-cover rounded-full shadow-inner border-2 border-white" alt="Profile Preview" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              {/* Upload Area */}
              <div className="flex-1">
                <div className="flex items-center">
                  <label className="cursor-pointer">
                    <span className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition">
                      Choose File
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFile}
                      ref={fileInputRef}
                      className="hidden"
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">
                    {fileName || "PNG, JPG up to 2MB"}
                  </span>
                </div>
                {errors.profilePic && <span className="text-xs text-red-500 mt-1">{errors.profilePic}</span>}
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="md:col-span-2 flex gap-4 mt-2">
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5 focus:ring-2 focus:ring-blue-400 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
              disabled={submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Update Employee' : 'Add Employee'}
            </button>
            <button
              type="button"
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition focus:ring-2 focus:ring-blue-400"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}
